import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Settings, Code } from "lucide-react"
import { type AIMonitorConfig, type PromptServiceConfig } from "@/types/config"
import { ModeSettings } from "./mode-settings"
import { ThresholdSettings } from "./threshold-settings"
import { CostSettings } from "./cost-settings"
import { PromptSettings } from "./prompt-settings"

interface MonitorSettingsProps {
  config: AIMonitorConfig
  onUpdate: (config: Partial<AIMonitorConfig>) => void
  isLoading?: boolean
  // Props for legislative prompts
  billReviewPrompt?: PromptServiceConfig
  oppositionPrompt?: PromptServiceConfig
  supportPrompt?: PromptServiceConfig
  onPromptUpdate?: (key: "LegislativeReview" | "Opposition" | "Support" | "ShouldMonitor", updates: Partial<PromptServiceConfig>) => void
}

export function MonitorSettings({ config, onUpdate, isLoading, billReviewPrompt, oppositionPrompt, supportPrompt, onPromptUpdate }: MonitorSettingsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Monitor Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="general">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general" className="flex-1 whitespace-nowrap text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium">
              <Settings className="h-4 w-4" />
              General Settings
            </TabsTrigger>
            <TabsTrigger value="developer" className="flex-1 whitespace-nowrap text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium">
              <Code className="h-4 w-4" />
              Developer Settings
            </TabsTrigger>
          </TabsList>
          <TabsContent value="general" className="space-y-4">
            <ModeSettings
              monitoringEnabled={config.MonitoringEnabled}
              onUpdate={onUpdate}
              isLoading={isLoading}
            />
            <ThresholdSettings 
              thresholds={config.Thresholds}
              onUpdate={onUpdate}
              isLoading={isLoading}
            />
            <CostSettings 
              costControl={config.CostControl}
              onUpdate={onUpdate}
              isLoading={isLoading}
            />
          </TabsContent>
          <TabsContent value="developer">
            <PromptSettings 
              promptText={config.PromptConfig?.PromptText}
              onUpdate={(updates) => {
                console.log('Monitor settings received prompt update:', updates);
                onUpdate(updates);
              }}
              isLoading={isLoading}
              billReviewPrompt={billReviewPrompt}
              oppositionPrompt={oppositionPrompt}
              supportPrompt={supportPrompt}
              onPromptUpdate={onPromptUpdate}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}